import React, { Component, Fragment, useState, useEffect, useRef, useMemo, } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Table, Button, Input, Nav, NavItem, NavLink, } from 'reactstrap';
import numeral from 'numeral';
import { mapValues, omit, pickBy, mergeWith, last, chunk, uniqBy, orderBy, uniq, isEqual, get, sumBy, isEmpty, isNumber as _isNumber, groupBy, sortBy, keyBy, range, debounce, pick, } from 'lodash';
import { toast } from 'react-toastify';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import fileDownload from 'js-file-download';
import { unparse as unparseCsv, parse as parseCsv } from 'papaparse';
import { TextEncoder, TextDecoder } from 'text-encoding';
import qs from 'qs';
import Select from 'react-select';
import { useCounter, useToggle, useDebounce, useMap, useAsync, } from 'react-use';
import { useLocation, useHistory } from 'react-router-dom';
import { format as formatDate, startOfMonth, endOfMonth, addYears, addMonths, } from 'date-fns';
import { ResizableBox } from 'react-resizable';

import firebase, { functions } from '../../firebase';
import { batch, getCollectionData, getAllCollectionDataByChunk, } from '../../shared/firebase';
import { mapKeysToJa, } from '../../shared/texts';
import { dimensionsOfCompany, enabledBudgetSubjectTypes, canUseForecast, } from '../../shared/models/company';
import { computeRelatedItems, } from '../../shared/models/customAccountItem';
import { breakdownItemsLimitUnit, documentTypes, budgetSubjectTypes, screenQueryUncontrols, } from '../../shared/config';
import { startOfMonthByFiscalYears, existsInFiscalYears, fiscalYearOfPeriod, readFile, fullPathWithParams, trialItemsToRowItems, computeSegmentedTrialAmount, computeBudget, computeCustomAmount, pickSearch, filterRowsByAmount, groupSegmentedTrials, groupSegmentedTrialSubItems, } from '../../utils';
import { canWriteNote, } from '../../abilities';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useCollectionsFetch from '../hooks/useCollectionsFetch';
import useQueryParams from '../hooks/useQueryParams';
import useMainTableUtilities from '../hooks/useMainTableUtilities';
import TrialQueriesSection from '../TrialQueriesSection';
import HelpLink from '../HelpLink';
import ProgressButton from '../ProgressButton';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import UserDisplay from '../UserDisplay';
import ModelFormModal from '../modals/ModelFormModal';
import CompanyPage from '../hocs/CompanyPage';
import CommentsDropdown from '../CommentsDropdown';
import QuerySelector from '../QuerySelector';
import HoveredNoter from '../HoveredNoter';
import AccountItemDisplay from '../AccountItemDisplay';
import OverlayLoading from '../OverlayLoading';
import Row from './CompanyMonthBudgets/Row';
import ShareButton from '../ShareButton';
import AllCollapseButton from '../AllCollapseButton';
import BudgetButton from '../BudgetButton';

const suffix = documentType => ({ pl: '', cr: 'Cr' })[documentType];
const fetchFreeeSegmentedTrials = functions.httpsCallable('fetchFreeeSegmentedTrials', { timeout: 550000 });
const logCsvExport = functions.httpsCallable('logCsvExport');
const db = firebase.firestore();
const auth = firebase.auth();
const companiesRef = db.collection('companies');
const { keys, entries, } = Object;
const { abs } = Math;
const isFiniteNumber = _ => _isNumber(_) && isFinite(_);
const maxContainersCount = 12;
const COLUMN_WIDTH = 150;
const AMOUNT_HEIGHT = '24px';
const metrics = {
  budgetAmount: { label: '予算' },
  comparedBudgetAmount: { label: '比較予算' },
  diffBetweenBudgets: { label: '予算差額' },
  amount: { label: '当期実績' },
  budgetDiff: { label: '差額' },
  achievementRate: { label: '達成率', isRate: true, },
  estimatedAmount: { label: '着地見込' },
  prevYearAmount: { label: '前期実績' },
  prevYearDiff: { label: '増減額' },
  prevYearDiffRate: { label: '増減率', isRate: true, },
};
const metricOptions = entries(metrics).map(([k, v]) => ({ label: v.label, value: k }));
const aggregationTypes = {
  cumulative: { label: '累計', },
  single: { label: '単月', },
};
const aggregationTypeOptions = entries(aggregationTypes).map(([k, v]) => ({ label: v.label, value: k }));

export default CompanyPage(CompanyMonthBudgets);

function CompanyMonthBudgets(props) {
  const { accountItemsOrderSetting, accountItems, customAccountItems, company, company: { sourceId: companySourceId }, user, members, period, targetMonth, match: { params: { companyId } } } = props;
  // 見込機能はスタンダード・アドバンスのみ。プラン判定と事業所設定トグルの両方を満たす場合のみ有効。
  const useForecast = canUseForecast(company) && company.useForecast;
  const history = useHistory();
  const location = useLocation();
  const queryParams = useQueryParams();
  const {
    tabKey = 'section__pl',
    dimension = 'none',
    budgetSubjectIds,
    budgetContainerId,
    comparedBudgetContainerId,
    aggregationType = 'cumulative',
    itemNamesForFilter,
    subItemNamesForFilter,
  } = queryParams;
  const {
    onClickComment,
    toggleCommenter,
    toggleInitiallyCommentRow,
    subRowsVisibilities,
    setAllSubRowsVisibilities,
    setSubRowsVisibilities,
    setHoveredCommenters,
    onRowsChanged,
  } = useMainTableUtilities();
  const [screenVersion, { inc: updateScreenVersion }] = useCounter();
  const [leftColumnWidth, setLeftColumnWidth] = useState(COLUMN_WIDTH * 2);
  const [allRows, setAllRows] = useState([]);
  const isSubFiltered = (queryParams.dimension || 'none') !== 'none' && queryParams.isSubFilter === '1';
  const relatedCustomAccountItems = useMemo(_ => customAccountItems.filter(_ => (_.dimension || 'none') === 'none' || _.dimension === dimension), [customAccountItems, dimension]);
  const openItemKeys = useMemo(_ => {
    if((queryParams.dimension || 'none') === 'none' || isSubFiltered) return [];
    
    // 予実画面に表示されるカスタム科目の関連 itemKeys を計算
    const customAccountItemsByName = keyBy(relatedCustomAccountItems, 'name');
    const customAccountItemKeys = relatedCustomAccountItems.flatMap(customAccountItem => {
      const relatedItems = computeRelatedItems(customAccountItem, customAccountItemsByName);
      return relatedItems.map(_ => ({
        accountItem: [_.value, 'none'].join('__'),
        accountCategory: ['none', _.value].join('__'),
      })[_.type]);
    });
    
    return uniq([...keys(pickBy(subRowsVisibilities, _ => _)), ...customAccountItemKeys]);
  }, [queryParams.dimension, subRowsVisibilities, isSubFiltered, relatedCustomAccountItems]);
  const [subRowsBreakdownItemsLimitRates, { set: setSubRowsBreakdownItemsLimitRates }] = useMap({});
  const [budgetSubjectType, documentType] = tabKey.split('__');
  const filterForScreen = _ => (_.accountItemCategory?.type === documentType && !_.accountItemCategory?.hidden) || _.isCustom;
  const rows = useMemo(_ => allRows.filter(filterForScreen), [allRows, documentType]);
  const { freeeCollectionName, label: budgetSubjectTypeLabel, idName: subjectIdName, trialsCollectionName, freeeSubjectIdsName, collectionName: subjectCollectionName, nameJa, budgetCollectionName, } = budgetSubjectTypes[budgetSubjectType];
  const dimensionOptions = entries(dimensionsOfCompany(company)).filter(_ => !['sections', 'segment1s', 'segment2s', 'segment3s'].filter(_ => (budgetSubjectType + 's') === _).includes(_[0])).map(([k, v]) => ({ label: v, value: k }));
  const [isCommentRowToggled, toggleCommentRowToggled] = useToggle();
  const isComparing = !isEmpty(comparedBudgetContainerId);
  const [isSettingRows, toggleSettingRows] = useToggle(false);
  const [isFetchingLastTrials, toggleFetchingLastTrials] = useToggle();
  const [isExporting, toggleExporting] = useToggle();
  const [showsModal, toggleModal] = useToggle();
  const { start_date: startDate, } = fiscalYearOfPeriod(period, company.fiscalYears);
  const prevYearMonths = range(-12, 0).map(_ => endOfMonth(addMonths(startDate, _)));
  const currentYearMonths = range(0, 12).map(_ => endOfMonth(addMonths(startDate, _)));
  const budgetSubjects = useCollectionSubscription(company.ref.collection(subjectCollectionName), [budgetSubjectType]);
  const sortedBudgetSubjects = sortBy(budgetSubjects || [], _ => _.index != null ? _.index : (budgetSubjects || []).indexOf(_));
  const budgetContainers = useCollectionSubscription(companiesRef.doc(companyId).collection('budgetContainers').orderBy('index'));
  const _freeeSubjects = useCollectionSubscription(companiesRef.doc(companyId).collection(freeeCollectionName), [budgetSubjectType], { initialValue: null });
  const freeeSubjects = _freeeSubjects || [];
  const freeeSubjectsById = keyBy([{ id: 0, name: '未選択' }, ...freeeSubjects], 'id');
  const { value: budgets, } = useAsync(async () => {
    if(!budgetContainerId || isEmpty(budgetSubjectIds)) return [];

    return (await Promise.all(chunk(budgetSubjectIds, 10).map(async (budgetSubjectIds) => {
      return getCollectionData(companiesRef.doc(companyId).collection('budgets').where('period', '==', period).where('budgetContainerId', '==', budgetContainerId).where('budgetSubjectType', '==', budgetSubjectType).where('budgetSubjectId', 'in', budgetSubjectIds).where('dimension', '==', dimension));
    }))).flat();
  }, [screenVersion, period, budgetContainerId, budgetSubjectIds, dimension]);
  const budgetsGroupedByClosingDate = groupBy(budgets, _ => formatDate(_.closingDate, 'YYYYMM'));
  const budgetsGroupedByItemKey = groupBy(budgets, 'itemKey');
  const { value: comparedBudgets, } = useAsync(async () => {
    if(!comparedBudgetContainerId || isEmpty(budgetSubjectIds)) return [];

    return (await Promise.all(chunk(budgetSubjectIds, 10).map(async (budgetSubjectIds) => {
      return getCollectionData(companiesRef.doc(companyId).collection('budgets').where('period', '==', period).where('budgetContainerId', '==', comparedBudgetContainerId).where('budgetSubjectType', '==', budgetSubjectType).where('budgetSubjectId', 'in', budgetSubjectIds).where('dimension', '==', dimension));
    }))).flat();
  }, [screenVersion, period, comparedBudgetContainerId, budgetSubjectIds, dimension]);
  const comparedBudgetsGroupedByClosingDate = groupBy(comparedBudgets, _ => formatDate(_.closingDate, 'YYYYMM'));
  const comparedBudgetsGroupedItemKey = groupBy(comparedBudgets, 'itemKey');
  const { value: forecasts, } = useAsync(async () => {
    if(!useForecast || isEmpty(budgetSubjectIds)) return [];

    return (await Promise.all(chunk(budgetSubjectIds, 10).map(async (budgetSubjectIds) => {
      return getCollectionData(companiesRef.doc(companyId).collection('budgets').where('period', '==', period).where('budgetContainerId', '==', 'forecast').where('budgetSubjectType', '==', budgetSubjectType).where('budgetSubjectId', 'in', budgetSubjectIds).where('dimension', '==', dimension));
    }))).flat();
  }, [screenVersion, period, budgetSubjectIds, dimension, useForecast]);
  const forecastsGroupedByItemKey = groupBy(forecasts, 'itemKey');
  const trialsRef = (documentType) => {
    return company.ref
      .collection(trialsCollectionName + suffix(documentType))
        .where('dimension', '==', 'none')
        .where('closingDate', '>=', formatDate(prevYearMonths[0], 'YYYY-MM-DD'))
        .where('closingDate', '<=', formatDate(last(currentYearMonths), 'YYYY-MM-DD'));
  };
  const plTrials = useCollectionSubscription(trialsRef('pl'), [tabKey, dimension, period]);
  const crTrials = useCollectionSubscription(trialsRef('cr'), [tabKey, dimension, period]);
  const trials = useMemo(_ => [...plTrials, ...crTrials], [plTrials, crTrials]);
  const trialsGroupedByClosingDate = groupBy(trials, _ => formatDate(_.closingDate, 'YYYYMM'));
  const trialSubjectRowsGroupedByItemKey = groupSegmentedTrials(trials.flatMap(t => (t[freeeCollectionName] || []).map(_ => ({ ...t, ...omit(_, 'id'), subjectId: _.id, }))));

  const subItemCollectionName = trialsCollectionName + 'SubItemChunks__' + dimension;
  const refs = useMemo(_ =>
    isSubFiltered ? (
      [company.ref.collection(subItemCollectionName)]
    ) : chunk(openItemKeys, 10).map(_ => company.ref.collection(subItemCollectionName).where('itemKey', 'in', _))
  , [isSubFiltered, subItemCollectionName, openItemKeys]);
  const _trialSubItems = useCollectionsFetch(refs.map((ref) => {
    return ref
      .where('closingDate', '>=', formatDate(prevYearMonths[0], 'YYYY-MM-DD'))
      .where('closingDate', '<=', formatDate(last(currentYearMonths), 'YYYY-MM-DD'));
  }), [refs, period]);
  const trialSubItems = useMemo(_ => _trialSubItems.flatMap(_ => _.subItems.map(__ => ({ ..._, ...__ }))), [_trialSubItems]);
  const trialSubItemsGroupedByClosingDate = groupBy(trialSubItems, _ => formatDate(_.closingDate, 'YYYYMM'));
  const trialSubItemsGroupedByItemKey = groupSegmentedTrialSubItems(trialSubItems);

  const comments = useCollectionSubscription(companiesRef.doc(companyId).collection('comments').where('queryKey', '==', budgetCollectionName).orderBy('createdAt'));
  const commentsGroupedByCommenteeKey = groupBy(comments, 'commenteeKey');
  const notes = useCollectionSubscription(companiesRef.doc(companyId).collection('notes').where('queryKey', '==', budgetCollectionName));
  const notesByKey = keyBy(notes, 'noteKey');
  const toggleTab = (tabKey) => {
    const [targetBudgetSubjectType] = tabKey.split('__');
    history.replace(location.pathname + '?' + qs.stringify({ ...queryParams, tabKey, ...(budgetSubjectType !== targetBudgetSubjectType && { budgetContainerId: null, budgetSubjectIds: null, }), }));
  };

  const filterRows = (rows) => {
    let filteredRows = rows;
    if(!isEmpty(itemNamesForFilter)) {
      filteredRows = filteredRows.filter(_ => itemNamesForFilter.includes(_.itemName));
    }
    if(!isSubFiltered) {
      entries(metrics).map(([metric, { isRate }]) => {
        filteredRows = filterRowsByAmount(filteredRows, queryParams, metric, _ => _.column[metric] * (isRate ? 100 : 1));
      });
    } else {
      filteredRows = entries(groupBy(filteredRows, 'itemKey'))
        .map(([itemKey, [mainRow, ...subRows]]) => {
          let filteredSubRows = subRows;
          if(!isEmpty(subItemNamesForFilter)) {
            filteredSubRows = filteredSubRows.filter(row => subItemNamesForFilter.some(_ => row.subItemName === _));
          }
          isSubFiltered && entries(metrics).map(([metric, { isRate }]) => {
            filteredSubRows = filterRowsByAmount(filteredSubRows, queryParams, metric, _ => _.column[metric] * (isRate ? 100 : 1));
          });
          return {
            mainRow: (
              subRows.length === 0 ? mainRow : {
                ...mainRow,
                column: ((column) => {
                  const amount = !isSubFiltered ? column.amount : sumBy(filteredSubRows, _ => _.column.amount);
                  const prevYearAmount = !isSubFiltered ? column.prevYearAmount : sumBy(filteredSubRows, _ => _.column.prevYearAmount);
                  const budgetAmount = !isSubFiltered ? column.budgetAmount : sumBy(filteredSubRows, _ => _.column.budgetAmount);
                  const comparedBudgetAmount = !isSubFiltered ? column.comparedBudgetAmount : sumBy(filteredSubRows, _ => _.column.comparedBudgetAmount);
                  const diffBetweenBudgets = budgetAmount - comparedBudgetAmount;
                  const budgetDiff = amount - budgetAmount;
                  const achievementRate = amount / budgetAmount;
                  const estimatedAmount = !isSubFiltered ? column.estimatedAmount : sumBy(filteredSubRows, _ => _.column.estimatedAmount);
                  const prevYearDiff = amount - prevYearAmount;
                  const prevYearDiffRate = prevYearDiff / prevYearAmount;
                  return {
                    ...column,
                    amount,
                    prevYearAmount,
                    budgetAmount,
                    comparedBudgetAmount,
                    diffBetweenBudgets,
                    budgetDiff,
                    achievementRate,
                    estimatedAmount,
                    prevYearDiff,
                    prevYearDiffRate,
                  };
                })(mainRow.column),
              }
            ),
            subRows: filteredSubRows.map((row, i) => {
              return {
                ...row,
                breakDownItemIndex: i,
                subItemsCount: filteredSubRows.length,
              };
            }),
          };
        })
        .filter(_ => !isSubFiltered || _.subRows.length > 0)
        .reduce((x, y) => [...x, y.mainRow, ...y.subRows], []);
    }
    return filteredRows;
  };
  const filteredRows = filterRows(rows);

  const budgetSubjectOptions = sortedBudgetSubjects.map(_ => ({ label: _.name, value: _.id }));
  const budgetContainerOptions = budgetContainers.map(_ => ({ label: _.name, value: _.id }));
  const itemNameOptions = uniq(rows.map(_ => _.itemName)).map(_ => ({ label: _, value: _ }));
  const subItemNameOptions = uniq(rows.map(_ => _.subItemName).filter(_ => _)).map(_ => ({ label: _, value: _ }));
  const generateMonthsItems = (options) => {
    const { budgetsGroupedByClosingDate } = options;
    return [prevYearMonths, currentYearMonths].map((months) => {
      return months.map((closingDate) => {
        const balances = trialsGroupedByClosingDate[formatDate(closingDate, 'YYYYMM')] || [];
        const budgets = budgetsGroupedByClosingDate[formatDate(closingDate, 'YYYYMM')] || [];
        const comparedBudgets = comparedBudgetsGroupedByClosingDate[formatDate(closingDate, 'YYYYMM')] || [];
        const trialSubItems = (options?.trialSubItemsGroupedByClosingDate || trialSubItemsGroupedByClosingDate)[formatDate(closingDate, 'YYYYMM')] || [];
        const exists = !isEmpty(balances);
        return {
          closingDate,
          exists,
          balances,
          balancesByItemKey: keyBy(balances, 'itemKey'),
          budgets,
          budgetsGroupedByItemKey: groupBy(budgets, 'itemKey'),
          comparedBudgets,
          comparedBudgetsGroupedByItemKey: groupBy(comparedBudgets, 'itemKey'),
          trialSubItemsGroupedByItemKey: groupBy(trialSubItems, 'itemKey'),
          dimension,
        };
      });
    });
  };
  const monthsItems = generateMonthsItems({ budgetsGroupedByClosingDate });
  const budgetSubjectsById = keyBy(budgetSubjects, 'id');
  const selectedBudgetSubjects = (budgetSubjectIds || []).map(_ => budgetSubjectsById[_]);
  const generateRows = (options) => {
    const { monthsItems, budgetSubjects, budgetsGroupedByItemKey, forecastsGroupedByItemKey, } = options;
    const trialSubItemsGroupedByItemKey = options?.trialSubItems && groupSegmentedTrialSubItems(options.trialSubItems);
    const items = trialItemsToRowItems(monthsItems.flat(), { targetMonth, accountItems, customAccountItems: relatedCustomAccountItems, screenType: 'sectionBudgets', documentType, itemType: documentType, dimension, budgetSubjectsFreeeName: freeeCollectionName, sortsAllPlAndCrCategories: true, accountItemsOrderSetting, })
    const mainItems = items.filter(_ => !_.isCategory && !_.isSubRow && !_.isCustom);
    const mainItemsByItemKey = keyBy(mainItems, 'itemKey');
    const rows = items
      .map(itemToRow.bind(null, mainItems, mainItemsByItemKey, { budgetSubjects, budgetsGroupedByItemKey, forecastsGroupedByItemKey, trialSubItemsGroupedByItemKey, }))
      .filter(_ => abs(_.column.amount) > 0 || abs(_.column.budgetAmount) > 0 || abs(_.column.comparedBudgetAmount) > 0 || abs(_.column.prevYearAmount) > 0);
    return entries(groupBy(
      Object.values(groupBy(rows, 'mainRowItemKey')).map(([mainItem, ...subItems]) => {
        return [mainItem, ...orderBy(subItems, [_ => Math.abs(_.column.amount), _ => Math.abs(_.column.budgetAmount)], ['desc', 'desc'])];
      }).flat()
    , 'itemKey'))
      .reduce((x, [itemKey, [mainRow, ...subRows]]) => {
        const _subRows = subRows.map((row, i) => {
          return {
            ...row,
            breakDownItemIndex: i,
            subItemsCount: subRows.length,
          };
        });
        return [
          ...x,
          { ...mainRow, subRows: _subRows },
          ..._subRows
        ];
      }, []);
  };
  const computeOverMonth = (date) => {
    return date > endOfMonth(targetMonth.toString().replace(/\d{2}$/, _ => '-' + _));
  };
  const isWithinTargetMonth = (date) => {
    return ({
      cumulative: date <= endOfMonth(targetMonth.toString().replace(/\d{2}$/, _ => '-' + _)),
      single: startOfMonth(targetMonth.toString().replace(/\d{2}$/, _ => '-' + _)) <= date && date <= endOfMonth(targetMonth.toString().replace(/\d{2}$/, _ => '-' + _)),
    })[aggregationType];
  };
  const itemToRow = (mainItems, mainItemsByItemKey, options, item) => {
    const { isCustom = false, accountItemCategory, itemName, itemKey, isSubRow, subItemName, hierarchyLevel, accountItemName, displayExpression = '0,0', } = item;
    const _trialSubItemsGroupedByItemKey = options.trialSubItemsGroupedByItemKey || trialSubItemsGroupedByItemKey;
    const accountCategoryName = accountItemName ? null : item.account_category_name || item.accountCategoryName;
    const isCategory = isEmpty(accountItemName);
    const rowKey = (isCustom ? 'cus__' : '') + (isCategory ? 'cat__' : '') + ((dimension !== 'none' && isSubRow) ? `${itemName}__${dimension}__${subItemName}` : itemName);
    const mainRowItemKey = isCustom ? itemKey : item.mainRowItemKey;
    const rowName = subItemName || itemName;
    const _budgetSubjects = options.budgetSubjects || budgetSubjects;
    const _budgetsGroupedByItemKey = options.budgetsGroupedByItemKey || budgetsGroupedByItemKey;
    const _forecastsGroupedByItemKey = options.forecastsGroupedByItemKey || forecastsGroupedByItemKey;
    const targetRangeMonths = currentYearMonths.filter(_ => isWithinTargetMonth(_));
    const outRangeMonths = currentYearMonths.filter(_ => !isWithinTargetMonth(_));
    const prevYearTargetRangeMonths = targetRangeMonths.map(_ => addMonths(_, -12));
    const targetMonthEnd = endOfMonth(targetMonth.toString().replace(/\d{2}$/, _ => '-' + _));
    const pastRangeMonths = targetRangeMonths.filter(_ => _ <= targetMonthEnd);
    const futureRangeMonths = targetRangeMonths.filter(_ => _ > targetMonthEnd);
    const pastAllMonths = currentYearMonths.filter(_ => _ <= targetMonthEnd);
    const futureAllMonths = currentYearMonths.filter(_ => _ > targetMonthEnd);
    const column = (() => {
      const key = `${rowKey}_${targetMonth}`;
      const budgetSubjectRows = _budgetSubjects.map((budgetSubject) => {
        const freeeSubjects = budgetSubject?.[freeeSubjectIdsName].map(_ => freeeSubjectsById[_]).filter(_ => _) || [];
        const commenteeKey = [rowKey, budgetSubject?.id, targetMonth].join('_');
        const noteKey = [rowKey, budgetSubject?.id, targetMonth].join('_');
        const prevYearAmount = computeSegmentedTrialAmount(budgetSubjectType, item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, prevYearTargetRangeMonths, customAccountItems, budgetSubject?.[freeeSubjectIdsName], [budgetSubject?.id]);
        const budgetAmount = computeBudget(budgetSubjectType, item, _budgetsGroupedByItemKey, dimension, targetRangeMonths, customAccountItems, [budgetSubject?.id], mainItems, mainItemsByItemKey);
        const comparedBudgetAmount = computeBudget(budgetSubjectType, item, comparedBudgetsGroupedItemKey, dimension, targetRangeMonths, customAccountItems, [budgetSubject?.id], mainItems, mainItemsByItemKey);
        const diffBetweenBudgets = budgetAmount - comparedBudgetAmount;
        const outRangeBudgetAmount = computeBudget(budgetSubjectType, item, _budgetsGroupedByItemKey, dimension, outRangeMonths, customAccountItems, [budgetSubject?.id], mainItems, mainItemsByItemKey);
        const pastAmount = computeSegmentedTrialAmount(budgetSubjectType, item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, pastRangeMonths, customAccountItems, budgetSubject?.[freeeSubjectIdsName], [budgetSubject?.id]);
        // 将来月の見込: 月ごとに見込が未入力(0)なら予算で補完して合算（着地見込と同じ挙動）
        const computeForecastWithFallback = (months) => useForecast ? sumBy(months, (month) => {
          const f = computeBudget(budgetSubjectType, item, _forecastsGroupedByItemKey, dimension, [month], customAccountItems, [budgetSubject?.id], mainItems, mainItemsByItemKey, 'forecast');
          return f || computeBudget(budgetSubjectType, item, _budgetsGroupedByItemKey, dimension, [month], customAccountItems, [budgetSubject?.id], mainItems, mainItemsByItemKey);
        }) : 0;
        const futureForecastAmount = computeForecastWithFallback(futureRangeMonths);
        const futureAllForecastAmount = computeForecastWithFallback(futureAllMonths);
        const pastAllAmount = computeSegmentedTrialAmount(budgetSubjectType, item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, pastAllMonths, customAccountItems, budgetSubject?.[freeeSubjectIdsName], [budgetSubject?.id]);
        // NOTE: targetRangeMonthsはtargetMonth以前しか含まない(=futureRangeMonthsは常に空)ため、amountは常に実績のみ。
        // 見込は年度通算の着地見込(estimatedAmount)として別列で表示する
        const amount = useForecast
          ? (pastAmount + futureForecastAmount)
          : computeSegmentedTrialAmount(budgetSubjectType, item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, targetRangeMonths, customAccountItems, budgetSubject?.[freeeSubjectIdsName], [budgetSubject?.id]);
        const forecastAmount = useForecast ? amount : 0;
        const budgetDiff = amount - budgetAmount;
        const achievementRate = amount / budgetAmount;
        const estimatedAmount = useForecast ? (
          pastAllAmount + futureAllForecastAmount
        ) : ({
          cumulative: _ => amount + outRangeBudgetAmount,
          single: _ => amount,
        })[aggregationType]();
        const prevYearDiff = amount - prevYearAmount;
        const prevYearDiffRate = prevYearDiff / prevYearAmount;
        const note = (notesByKey[noteKey] || {}).value;
        return { budgetSubject, key, commenteeKeys: [commenteeKey], noteKeys: [noteKey], amount, budgetAmount, comparedBudgetAmount, diffBetweenBudgets, forecastAmount, budgetDiff, achievementRate, estimatedAmount, prevYearAmount, prevYearDiff, prevYearDiffRate, note, freeeSubjects, amountBase, };
      });
      // NOTE: カスタム科目（比率系など）は部門ごとの値を足すと 率+率 になってしまうため、
      // 全部門をまとめて1回で計算し直す（分子・分母を合算してから割る）。通常科目は合算と一致する。
      const combinedCustomAmounts = isCustom ? (_ => {
        const allFreeeSubjectIds = _budgetSubjects.flatMap(_ => _?.[freeeSubjectIdsName] || []);
        const allBudgetSubjectIds = _budgetSubjects.map(_ => _?.id);
        const prevYearAmount = computeSegmentedTrialAmount(budgetSubjectType, item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, prevYearTargetRangeMonths, customAccountItems, allFreeeSubjectIds, allBudgetSubjectIds);
        const budgetAmount = computeBudget(budgetSubjectType, item, _budgetsGroupedByItemKey, dimension, targetRangeMonths, customAccountItems, allBudgetSubjectIds, mainItems, mainItemsByItemKey);
        const comparedBudgetAmount = computeBudget(budgetSubjectType, item, comparedBudgetsGroupedItemKey, dimension, targetRangeMonths, customAccountItems, allBudgetSubjectIds, mainItems, mainItemsByItemKey);
        const outRangeBudgetAmount = computeBudget(budgetSubjectType, item, _budgetsGroupedByItemKey, dimension, outRangeMonths, customAccountItems, allBudgetSubjectIds, mainItems, mainItemsByItemKey);
        // NOTE: カスタム科目（比率系など）は完成した率同士を足せないため、
        // 計算式の各項（分子・分母）の段階で 過去月の実績 + 将来月の見込 を合算し、除算は最後に1回だけ行う。
        const computeActualPlusForecast = (pastMonths, futureMonths) => computeCustomAmount(item, [...pastMonths, ...futureMonths], customAccountItems, allBudgetSubjectIds, 'performance', (amountType, _item) => {
          const pastAmount = isEmpty(pastMonths) ? 0 : computeSegmentedTrialAmount(budgetSubjectType, _item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, pastMonths, customAccountItems, allFreeeSubjectIds, allBudgetSubjectIds);
          if(isEmpty(futureMonths)) return pastAmount;
          const budgetItem = _item.isCustomMain ? mainItemsByItemKey[_item.itemKey] : _item;
          const futureForecast = !budgetItem ? 0 : (
            computeBudget(budgetSubjectType, budgetItem, _forecastsGroupedByItemKey, dimension, futureMonths, customAccountItems, allBudgetSubjectIds, mainItems, mainItemsByItemKey, 'forecast')
              || computeBudget(budgetSubjectType, budgetItem, _budgetsGroupedByItemKey, dimension, futureMonths, customAccountItems, allBudgetSubjectIds, mainItems, mainItemsByItemKey)
          );
          return pastAmount + futureForecast;
        });
        const amount = useForecast
          ? computeActualPlusForecast(pastRangeMonths, futureRangeMonths)
          : computeSegmentedTrialAmount(budgetSubjectType, item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, targetRangeMonths, customAccountItems, allFreeeSubjectIds, allBudgetSubjectIds);
        const estimatedAmount = useForecast ? (
          computeActualPlusForecast(pastAllMonths, futureAllMonths)
        ) : ({
          cumulative: _ => amount + outRangeBudgetAmount,
          single: _ => amount,
        })[aggregationType]();
        return {
          amountBase: amount, amount, prevYearAmount, budgetAmount, comparedBudgetAmount,
          diffBetweenBudgets: budgetAmount - comparedBudgetAmount,
          budgetDiff: amount - budgetAmount,
          estimatedAmount,
          prevYearDiff: amount - prevYearAmount,
        };
      })() : null;
      const sumOrCombined = key => combinedCustomAmounts ? combinedCustomAmounts[key] : sumBy(budgetSubjectRows, key);
      const _totalAmount = sumOrCombined('amount');
      const _totalBudgetAmount = sumOrCombined('budgetAmount');
      const _totalPrevYearAmount = sumOrCombined('prevYearAmount');
      const _totalPrevYearDiff = sumOrCombined('prevYearDiff');
      const budgetSubjectsSummaryRow = {
        key: `${rowKey}_${targetMonth}_summary`,
        isSummaryColumn: true,
        label: '合計',
        amount: _totalAmount,
        budgetAmount: _totalBudgetAmount,
        comparedBudgetAmount: sumOrCombined('comparedBudgetAmount'),
        diffBetweenBudgets: sumOrCombined('diffBetweenBudgets'),
        budgetDiff: sumOrCombined('budgetDiff'),
        achievementRate: _totalAmount / _totalBudgetAmount,
        estimatedAmount: sumOrCombined('estimatedAmount'),
        prevYearAmount: _totalPrevYearAmount,
        prevYearDiff: _totalPrevYearDiff,
        prevYearDiffRate: _totalPrevYearDiff / _totalPrevYearAmount,
      };
      const freeeSubjects = budgetSubjectRows.flatMap(_ => _.freeeSubjects);
      const commenteeKeys = budgetSubjectRows.map(_ => _.commenteeKeys[0]);
      const noteKeys = budgetSubjectRows.map(_ => _.noteKeys[0]);
      const [amountBase, amount, prevYearAmount, budgetAmount, comparedBudgetAmount, diffBetweenBudgets, budgetDiff, estimatedAmount, prevYearDiff] = ['amountBase', 'amount', 'prevYearAmount', 'budgetAmount', 'comparedBudgetAmount', 'diffBetweenBudgets', 'budgetDiff', 'estimatedAmount', 'prevYearDiff'].map(sumOrCombined);
      const achievementRate = amount / budgetAmount;
      const prevYearDiffRate = prevYearDiff / prevYearAmount;
      const notes = noteKeys.map(_ => (notesByKey[_] || {}).value);
      return { key, commenteeKeys, noteKeys, amount, budgetAmount, comparedBudgetAmount, diffBetweenBudgets, budgetDiff, achievementRate, estimatedAmount, prevYearAmount, prevYearDiff, prevYearDiffRate, notes, note: notes.join('\n'), freeeSubjects, budgetSubjectRows, budgetSubjectsSummaryRow, };
    })();
    const comments = column.commenteeKeys.flatMap(_ => commentsGroupedByCommenteeKey[_] || []);

    return {
      ...item,
      budgetSubjects: _budgetSubjects,
      accountItemCategory,
      accountItemName,
      accountCategoryName,
      hierarchyLevel,
      isCategory,
      isCustom,
      itemName,
      itemKey,
      mainRowItemKey,
      key: rowKey,
      subItemName,
      isSubRow,
      rowName,
      column,
      displayExpression,
      comments,
    };
  }
  const journalsScreenParams = {
    period,
    targetMonth,
    complexFilter: [
      [freeeCollectionName, 'in', selectedBudgetSubjects.flatMap(_ => _?.[freeeSubjectIdsName]?.map(_ => freeeSubjectsById[_]?.name))]
    ],
    usesArrayFilter: 1,
    complexFilterDescription: `${nameJa}: ${selectedBudgetSubjects.map(_ => _?.name).join(', ')}`,
    singleMonth: 1,
  };
  const rowsDependencies = [budgetSubjectIds, budgetContainerId, comparedBudgetContainerId, trials, trialSubItems, budgets, forecasts, targetMonth, aggregationType, comparedBudgets, documentType];
  useEffect(_ => toggleSettingRows(true), rowsDependencies);
  useDebounce(async () => {
    setAllRows(generateRows({ monthsItems, budgetSubjects: selectedBudgetSubjects, budgetsGroupedByItemKey, forecastsGroupedByItemKey, }));
    toggleSettingRows(false);
  }, 500, rowsDependencies);
  const onClickExport = async ({ isAll = false } = {}) => {
    toggleExporting(true);
    const isComparing = !isEmpty(comparedBudgetContainerId);
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const trialSubItems = dimension !== 'none' && (await getAllCollectionDataByChunk(company.ref
      .collection(subItemCollectionName)
      .where('closingDate', '>=', formatDate(prevYearMonths[0], 'YYYY-MM-DD'))
      .where('closingDate', '<=', formatDate(last(currentYearMonths), 'YYYY-MM-DD'))
    )).flatMap(_ => _.subItems.map(__ => ({ ..._, ...__ })));
    const trialSubItemsGroupedByClosingDate = groupBy(trialSubItems, _ => formatDate(_.closingDate, 'YYYYMM'));
    const allRows = isAll ? await (async () => {
      const budgets = await getCollectionData(companiesRef.doc(companyId).collection('budgets').where('period', '==', period).where('budgetContainerId', '==', budgetContainerId).where('budgetSubjectType', '==', budgetSubjectType).where('dimension', '==', dimension));
      const budgetsGroupedByItemKey = groupBy(budgets, 'itemKey');
      const allForecasts = useForecast ? await getCollectionData(companiesRef.doc(companyId).collection('budgets').where('period', '==', period).where('budgetContainerId', '==', 'forecast').where('budgetSubjectType', '==', budgetSubjectType).where('dimension', '==', dimension)) : [];
      const forecastsGroupedByItemKey = groupBy(allForecasts, 'itemKey');
      return sortedBudgetSubjects.map((budgetSubject) => {
        const monthsItems = generateMonthsItems({ budgetsGroupedByClosingDate, trialSubItemsGroupedByClosingDate, });
        return generateRows({ monthsItems, budgetSubjects: [budgetSubject], budgetsGroupedByItemKey, forecastsGroupedByItemKey, trialSubItems, });
      }).flat();
    })() : generateRows({ monthsItems, budgetSubjects: selectedBudgetSubjects, budgetsGroupedByItemKey, forecastsGroupedByItemKey, trialSubItems, });
    const data = filterRows(allRows.filter(filterForScreen)).map((row) => {
      const { budgetSubject } = row;
      return {
        aggregationType: aggregationTypes[aggregationType].label,
        yearMonth: targetMonth,
        budgetSubjectName: row.budgetSubjects?.map(_ => _.name).join(','),
        ...pick(row, ['itemName', 'subItemName']),
        ...(
          [...keys(omit(metrics, (!isComparing || isAll) && ['comparedBudgetAmount', 'diffBetweenBudgets'])), 'note'].reduce((x, y) => {
            return {
              ...x,
              [y]: row.column[y],
            };
          }, {})
        ),
      };
    });
    const fileContent = encoder.encode(unparseCsv(data.map(_ => mapKeysToJa(_, { amount: '当期実績', budgetSubjectName: nameJa, }))));
    try {
      await logCsvExport({ companyId: company.id, modelName: 'monthlyBudgets', payload: { period, targetMonth, budgetSubjects: (isAll ? [{ name: '全部門', }] : selectedBudgetSubjects.map(_ => omit(_, 'ref'))), } });
      fileDownload(fileContent, `${isAll ? '全' : ''}予実_${targetMonth}.csv`);
    } catch (e) {
      console.error(e);
      toast.error('失敗しました');
    }
    toggleExporting(false);
  };
  useDebounce(() => {
    const path = fullPathWithParams({
      dimension: dimension || 'none',
      aggregationType: aggregationType || 'cumulative',
      budgetContainerId: budgetContainers.some(_ => _.id === budgetContainerId) ? budgetContainerId : get(budgetContainers, [0, 'id']),
      budgetSubjectIds: budgetSubjectIds || (queryParams.budgetSubjectId && [queryParams.budgetSubjectId]) || [get(sortedBudgetSubjects, [0, 'id'])],
      ...queryParams,
    }, location);
    history.replace(encodeURI(path));
  }, 1000, [tabKey, budgetContainers, budgetSubjects]);
  useEffect(() => {
    toggleInitiallyCommentRow(rows);
    onRowsChanged();
  }, [rows]);

  return (
    <div className="company-monthly-budgets">
      <div className="px-3 py-3">
        <div className="d-flex justify-content-end mb-2">
          <HelpLink text="予実管理を行う" />
        </div>
        <div className="mb-2">
          <h3 className="text-center">予実</h3>
        </div>
        <Nav tabs>
          {
            entries(enabledBudgetSubjectTypes(company)).flatMap(([budgetSubjectType, { label: budgetSubjectTypeLabel }]) => {
              return entries(pick(documentTypes, ['pl', 'cr'])).map(([documentType, { label: documentTypeLabel }]) => {
                const key = [budgetSubjectType, documentType].join('__');
                const label = [budgetSubjectTypeLabel, documentTypeLabel].join(' ');
                return (
                  <NavItem key={key}>
                    <NavLink className={classnames('cursor-pointer', { active: tabKey === key })} onClick={toggleTab.bind(null, key)}>
                      {label}
                    </NavLink>
                  </NavItem>
                );
              });
            })
          }
        </Nav>
        <div className="py-3">
          <div className="mb-3 d-flex justify-content-between align-items-end">
            <TrialQueriesSection 
              company={company}
              screenType="monthBudgets"
              uncontrols={screenQueryUncontrols.monthBudgets}
              items={[
                [
                  {
                    name: 'budgetSubjectIds',
                    type: 'multiSelect',
                    options: budgetSubjectOptions,
                    label: budgetSubjectTypeLabel,
                    isFront: true,
                    props: { width: 200, selectorProps: { isClearable: false } },
                  },
                  {
                    name: 'itemNamesForFilter',
                    type: 'multiSelect',
                    options: itemNameOptions,
                    label: '科目名',
                    isFront: true,
                    props: { width: 300, }
                  },
                  {
                    name: 'dimension',
                    type: 'select',
                    options: dimensionOptions,
                    label: '明細',
                    isFront: true,
                    immediate: true,
                    props: { width: 150, selectorProps: { isClearable: false } },
                  },
                ],
                [
                  ...(
                    dimension !== 'none' ? [
                      {
                        name: 'isSubFilter',
                        type: 'boolean',
                        label: '明細単位で検索する',
                        immediate: true,
                      },
                    ] : []
                  ),
                  ...(
                    dimension !== 'none' && queryParams.isSubFilter === '1' ? [
                      {
                        name: 'subItemNamesForFilter',
                        type: 'multiSelect',
                        options: subItemNameOptions,
                        label: '明細名',
                        props: { width: 300, }
                      },
                    ] : []
                  ),
                ],
                [
                  {
                    name: 'budgetContainerId',
                    type: 'select',
                    options: budgetContainerOptions,
                    label: '予算',
                    isFront: true,
                    props: { width: 250, },
                  },
                  {
                    name: 'comparedBudgetContainerId',
                    type: 'select',
                    options: budgetContainerOptions,
                    label: '比較予算',
                    isFront: true,
                    props: { width: 250, },
                  },
                  {
                    name: 'aggregationType',
                    type: 'select',
                    options: aggregationTypeOptions,
                    label: '集計種別',
                    isFront: true,
                    props: { width: 150, selectorProps: { isClearable: false } },
                  },
                ],
                ...entries(metrics).map(([metric, { label, isRate }]) => {
                  return [
                    {
                      name: metric + 'Min',
                      type: 'numeric',
                      label: label + '下限' + (isRate ? '(%)' : ''),
                      props: { width: 200, }
                    },
                    {
                      name: metric + 'Max',
                      type: 'numeric',
                      label: label + '上限' + (isRate ? '(%)' : ''),
                      props: { width: 200, }
                    },
                  ];
                }),
              ]}
            />
            <div className="d-flex align-items-end gap-1 text-nowrap">
              <Button tag={Link} color="secondary" to={`/companies/${company.id}/journals?${qs.stringify(journalsScreenParams)}`} target="_blank" disabled={selectedBudgetSubjects.length === 0 || _freeeSubjects == null}>
                仕訳帳
                <span className="fas fa-external-link-alt ml-1" />
              </Button>
              <BudgetButton {...{ period, company, accountItems, customAccountItems, }} />
              <Button color="secondary" onClick={onClickExport.bind(null, { isAll: true })} disabled={isExporting}>
                全部門
                <span className={classnames('ml-1 fas', { 'fa-spin fa-spinner': isExporting, 'fa-download': !isExporting })} />
              </Button>
              <Button color="secondary" onClick={onClickExport} disabled={isExporting}>
                <span className={classnames('fas', { 'fa-spin fa-spinner': isExporting, 'fa-download': !isExporting })} />
              </Button>
              <ShareButton screenType="monthBudgets" {...{ company, subjectType: budgetSubjectType, subjects: selectedBudgetSubjects, }} />
              <CommentsDropdown company={company} companyId={companyId} currentUser={user} comments={comments} usersById={keyBy(members, 'id')} onClickComment={onClickComment(rows)} />
            </div>
          </div>
          <div className="d-flex justify-content-center position-relative" style={{ zIndex: 0 }}>
            <Table className="sticky-table table-hover" style={{ width: 'auto' }}>
              <thead className="text-center" style={{ lineHeight: '20px' }}>
                <tr>
                  <th className="text-left border-right p-0">
                    <ResizableBox className="p-2 d-flex align-items-center" width={COLUMN_WIDTH * 2} height={44} axis="x" resizeHandles={['e']} onResize={(e, _) => setLeftColumnWidth(_.size.width)}>
                      {
                        dimension !== 'none' && (
                          <AllCollapseButton size="sm" className="small px-0" color="link" rows={filteredRows} visibilities={subRowsVisibilities} setVisibilities={setAllSubRowsVisibilities} />
                        )
                      }
                      &nbsp;
                    </ResizableBox>
                  </th>
                  {
                    entries(omit(metrics, !isComparing && ['comparedBudgetAmount', 'diffBetweenBudgets'])).map(([metric, { label: metricLabel, }]) => {
                      return (
                        <th key={metric} className={classnames('text-nowrap')} style={{ width: COLUMN_WIDTH }}>
                          {metricLabel}
                        </th>
                      );
                    })
                  }
                </tr>
              </thead>
              <tbody>
                {
                  filteredRows.map((row) => {
                    const { isSubRow, itemKey, } = row;
                    const showsSubRows = (dimension !== 'none' && !!subRowsVisibilities[itemKey]);
                    if(isSubRow && !showsSubRows) return null;

                    const breakdownItemsLimitRate = subRowsBreakdownItemsLimitRates[itemKey] || 1;
                    const toggleSubRows = setSubRowsVisibilities.bind(null, itemKey, !showsSubRows);
                    const showMoreSubRows = setSubRowsBreakdownItemsLimitRates.bind(null, itemKey, breakdownItemsLimitRate + 1);
                    return <Row pageProps={props} row={row} {...{ company, commentsGroupedByCommenteeKey, notesByKey, budgetSubjectType, setHoveredCommenters, metrics, toggleSubRows, breakdownItemsLimitRate, showsSubRows, showMoreSubRows, leftColumnWidth, }} />
                  })
                }
              </tbody>
            </Table>
            <OverlayLoading isOpen={isSettingRows} />
          </div>
        </div>
      </div>
    </div>
  );
}
